/**
 * 📋 **목록은 대체하지 않고 합친다** — 저장소 목록이 바탕, 같은 id 는 게시본이 이긴다.
 *
 * ## 왜 (2026-08-21 실제 사고 · 2026-09-03 같은 위험을 굿즈에서 발견)
 * 책 목록이 이랬다: **게시본이 한 권이라도 있으면 저장소 목록을 통째로 버렸다.**
 * 그래서 운영자가 [+ 새 전자책] 으로 **빈 책 한 권을 저장한 순간**, 아이 화면에서
 * 「나무 위 도서관」과 「그레그와 병아리의 경주」가 **둘 다 사라졌다.** 지운 적도 없는데.
 * 에러도 안 나고 관리자에서도 똑같이 사라져서 무엇이 없어졌는지조차 알기 어렵다.
 *
 * 책은 그때 고쳤는데(`mergeBookLists`) **굿즈·행사는 안 고쳤다.** 굿즈는 지금
 * 상품 6개가 **저장소 파일에만** 있다 — 관리자가 목록을 못 받은 채(빈 목록)
 * 무언가 저장하면 그 6개가 아이 화면에서 통째로 사라진다.
 *
 * 그래서 규칙을 **한 곳**에 둔다. 목록마다 다시 짜면 또 한 곳만 고치게 된다.
 *
 * ## 규칙
 * - **저장소 목록이 바탕**이고, 같은 id 의 게시본이 있으면 그쪽이 이긴다(운영자가 고친 것)
 * - 게시본에만 있는 것은 **뒤에 덧붙인다**(운영자가 새로 만든 것)
 * - 저장소 목록의 순서를 지킨다 — 아이가 늘 보던 자리에 그것이 있다
 *
 * ## 이 규칙의 한계 (알고 고른 것)
 * 저장소 파일에 든 것은 **관리자에서 지워도 다시 나타난다** — 저장소 파일은 브라우저가
 * 못 지운다. 그건 개발자가 파일을 지워야 한다. 「지운 게 되살아난다」보다
 * **「지운 적 없는 게 사라진다」가 훨씬 나쁜 사고**라 이쪽을 택했다.
 */
export function mergeById<T extends { id?: string }>(
  fromRepo: readonly T[],
  fromPublished: readonly T[],
): T[] {
  const byId = new Map<string, T>();
  for (const x of fromPublished) if (x?.id) byId.set(x.id, x);

  const out: T[] = [];
  const used = new Set<string>();
  for (const x of fromRepo) {
    if (!x?.id || used.has(x.id)) continue;
    used.add(x.id);
    out.push(byId.get(x.id) ?? x); // 고친 게 있으면 그쪽이 이긴다
  }
  for (const x of fromPublished) {
    if (!x?.id || used.has(x.id)) continue;
    used.add(x.id);
    out.push(x); // 새로 만든 것
  }
  return out;
}
